/**
 * Layer Type Definitions
 * Enhanced layer system with polymorphic positioning and responsive overrides
 */

import type { LayerPosition } from './position';
import type { Brush } from './brush';

// Re-export position types for convenience
export type { LayerPosition } from './position';

// Format categories for responsive overrides
export type FormatCategory = 'tiny' | 'small' | 'medium' | 'wide' | 'tall' | 'square';

// Responsive overrides - format-specific adjustments
export interface ResponsiveOverrides {
  tiny?: Partial<LayerOverride>;
  small?: Partial<LayerOverride>;
  medium?: Partial<LayerOverride>;
  wide?: Partial<LayerOverride>;
  tall?: Partial<LayerOverride>;
  square?: Partial<LayerOverride>;
}

// Properties that can be overridden per format
export interface LayerOverride {
  position: LayerPosition;
  width: number | 'auto';
  height: number | 'auto';
  visible: boolean;
  opacity: number;
  transform: LayerTransform;
  // Type-specific overrides
  fontSize?: number;
  scale?: number;
}

// Transform properties
export interface LayerTransform {
  rotation?: number;
  scaleX?: number;
  scaleY?: number;
  originX?: number; // 0-1, default 0.5
  originY?: number; // 0-1, default 0.5
}

// Layer types
export type LayerType = 'text' | 'image' | 'video' | 'audio' | 'shape' | 'svg-path' | 'lottie' | 'rive' | 'primitive' | 'group';

// Base layer interface - common properties for all layers
export interface BaseLayer {
  id: string;
  type: LayerType;
  name: string;
  position: LayerPosition;
  transform?: LayerTransform;
  width?: number | 'auto';
  height?: number | 'auto';
  visible: boolean;
  locked: boolean;
  opacity: number;
  responsiveOverrides?: ResponsiveOverrides;
}

// Text content configuration
export interface TextContent {
  text: string;
  variableBinding?: string; // '{{headline}}', '{{cta}}', '{{subhead}}'
  fontSize: number;
  fontFamily: string;
  fontWeight: number;
  color: Brush;
  lineHeight?: number;
  letterSpacing?: number;
  textAlign?: 'left' | 'center' | 'right';
  textTransform?: 'none' | 'uppercase' | 'lowercase' | 'capitalize';
  maxWidth?: number;
  textShadow?: string;
}

// Text layer
export interface TextLayer extends BaseLayer {
  type: 'text';
  content: TextContent;
}

// Image content configuration
export interface ImageContent {
  src: string; // URL or asset ID
  alt?: string;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none';
  objectPosition?: string;
  borderRadius?: number;
  filter?: string;
  // SVG-specific properties
  isSvg?: boolean;
  svgContent?: string; // Raw SVG markup for inline rendering
  colorOverrides?: Record<string, string>; // originalColor -> newColor
  detectedColors?: string[]; // Colors found in the SVG
}

// Image layer
export interface ImageLayer extends BaseLayer {
  type: 'image';
  content: ImageContent;
}

// Video content configuration
export interface VideoContent {
  src: string; // URL or data URL
  poster?: string; // Poster image URL
  objectFit?: 'cover' | 'contain' | 'fill' | 'none';
  objectPosition?: string;
  borderRadius?: number;
  loop?: boolean;
  muted?: boolean;
  autoplay?: boolean;
  startTime?: number; // Start time in seconds
  endTime?: number; // End time in seconds
}

// Video layer
export interface VideoLayer extends BaseLayer {
  type: 'video';
  content: VideoContent;
}

// Audio content configuration
export interface AudioContent {
  src: string; // URL or data URL
  name?: string; // Display name for the audio file
  loop?: boolean;
  volume?: number; // 0-1
  startTime?: number; // Trim start (seconds)
  endTime?: number; // Trim end (seconds)
  duration?: number; // Total audio duration (seconds)
  fadeIn?: number; // Fade in duration (ms)
  fadeOut?: number; // Fade out duration (ms)
}

// Audio layer - appears in timeline only, no canvas representation
export interface AudioLayer extends BaseLayer {
  type: 'audio';
  content: AudioContent;
}

// Shape types
export type ShapeType = 'rectangle' | 'circle' | 'ellipse' | 'line' | 'polygon' | 'triangle' | 'pentagon' | 'hexagon' | 'star';

// Shape content configuration
export interface ShapeContent {
  shapeType: ShapeType;
  fill?: Brush;
  stroke?: Brush;
  strokeWidth?: number;
  borderRadius?: number;
  // Polygon-specific
  points?: number; // Number of sides for polygon
}

// Shape layer
export interface ShapeLayer extends BaseLayer {
  type: 'shape';
  content: ShapeContent;
}

// SVG Path Commands - 1:1 mapping to SVG path commands
export type SvgCommand =
  | { type: 'M'; x: number; y: number }                                    // Move to (absolute)
  | { type: 'm'; x: number; y: number }                                    // Move to (relative)
  | { type: 'L'; x: number; y: number }                                    // Line to (absolute)
  | { type: 'l'; x: number; y: number }                                    // Line to (relative)
  | { type: 'H'; x: number }                                               // Horizontal line (absolute)
  | { type: 'h'; x: number }                                               // Horizontal line (relative)
  | { type: 'V'; y: number }                                               // Vertical line (absolute)
  | { type: 'v'; y: number }                                               // Vertical line (relative)
  | { type: 'C'; x1: number; y1: number; x2: number; y2: number; x: number; y: number }  // Cubic bezier (absolute)
  | { type: 'c'; x1: number; y1: number; x2: number; y2: number; x: number; y: number }  // Cubic bezier (relative)
  | { type: 'S'; x2: number; y2: number; x: number; y: number }            // Smooth cubic (absolute)
  | { type: 's'; x2: number; y2: number; x: number; y: number }            // Smooth cubic (relative)
  | { type: 'Q'; x1: number; y1: number; x: number; y: number }            // Quadratic bezier (absolute)
  | { type: 'q'; x1: number; y1: number; x: number; y: number }            // Quadratic bezier (relative)
  | { type: 'T'; x: number; y: number }                                    // Smooth quadratic (absolute)
  | { type: 't'; x: number; y: number }                                    // Smooth quadratic (relative)
  | { type: 'A'; rx: number; ry: number; rotation: number; largeArc: boolean; sweep: boolean; x: number; y: number }  // Arc (absolute)
  | { type: 'a'; rx: number; ry: number; rotation: number; largeArc: boolean; sweep: boolean; x: number; y: number }  // Arc (relative)
  | { type: 'Z' }                                                          // Close path
  | { type: 'z' };                                                         // Close path (same as Z)

// SVG Path content configuration
export interface SvgPathContent {
  commands: SvgCommand[];
  fill?: Brush;
  fillRule?: 'nonzero' | 'evenodd';
  stroke?: Brush;
  strokeWidth?: number;
  strokeLinecap?: 'butt' | 'round' | 'square';
  strokeLinejoin?: 'miter' | 'round' | 'bevel';
  strokeDasharray?: number[];
  strokeDashoffset?: number;
}

// SVG Path layer - complex vector paths
export interface SvgPathLayer extends BaseLayer {
  type: 'svg-path';
  content: SvgPathContent;
}

// Convert SvgCommand array to SVG path d string
export function svgCommandsToPath(commands: SvgCommand[]): string {
  return commands.map(cmd => {
    switch (cmd.type) {
      case 'M':
      case 'm':
        return `${cmd.type} ${cmd.x} ${cmd.y}`;
      case 'L':
      case 'l':
        return `${cmd.type} ${cmd.x} ${cmd.y}`;
      case 'H':
      case 'h':
        return `${cmd.type} ${cmd.x}`;
      case 'V':
      case 'v':
        return `${cmd.type} ${cmd.y}`;
      case 'C':
      case 'c':
        return `${cmd.type} ${cmd.x1} ${cmd.y1} ${cmd.x2} ${cmd.y2} ${cmd.x} ${cmd.y}`;
      case 'S':
      case 's':
        return `${cmd.type} ${cmd.x2} ${cmd.y2} ${cmd.x} ${cmd.y}`;
      case 'Q':
      case 'q':
        return `${cmd.type} ${cmd.x1} ${cmd.y1} ${cmd.x} ${cmd.y}`;
      case 'T':
      case 't':
        return `${cmd.type} ${cmd.x} ${cmd.y}`;
      case 'A':
      case 'a':
        return `${cmd.type} ${cmd.rx} ${cmd.ry} ${cmd.rotation} ${cmd.largeArc ? 1 : 0} ${cmd.sweep ? 1 : 0} ${cmd.x} ${cmd.y}`;
      case 'Z':
      case 'z':
        return cmd.type;
      default:
        return '';
    }
  }).join(' ');
}

// Lottie animation content configuration
export interface LottieContent {
  // Either inline data or external source
  src?: string;                    // URL to .lottie or .json file
  data?: object;                   // Inline Lottie JSON data
  // Playback options
  autoplay?: boolean;
  loop?: boolean | number;         // true, false, or loop count
  speed?: number;                  // Playback speed multiplier
  direction?: 1 | -1;              // 1 = forward, -1 = reverse
  // Segment control
  segment?: [number, number];      // [startFrame, endFrame]
  // Render options
  renderer?: 'svg' | 'canvas';     // Rendering mode
  // Interactivity
  hover?: boolean;                 // Play on hover
  playOnClick?: boolean;           // Play/pause on click
}

// Lottie animation layer
export interface LottieLayer extends BaseLayer {
  type: 'lottie';
  content: LottieContent;
}

// Rive animation content configuration
export interface RiveContent {
  src: string;                     // URL to .riv file
  // State machine control
  stateMachine?: string;           // Name of state machine to use
  animations?: string[];           // Animation names to play
  // Playback options
  autoplay?: boolean;
  // Fit options
  fit?: 'cover' | 'contain' | 'fill' | 'none' | 'scaleDown';
  alignment?: 'center' | 'topLeft' | 'topCenter' | 'topRight' |
              'centerLeft' | 'centerRight' |
              'bottomLeft' | 'bottomCenter' | 'bottomRight';
  // Interactivity - Rive handles this via state machines
  // Inputs can be set via the state machine
}

// Rive animation layer
export interface RiveLayer extends BaseLayer {
  type: 'rive';
  content: RiveContent;
}

// Primitive types - reusable brand components
export type PrimitiveType =
  | 'eclipse-moon'
  | 'star-field'
  | 'wordmark'
  | 'app-badges'
  | 'data-leak'
  | 'cta-button';

// Primitive content configuration
export interface PrimitiveContent {
  primitiveType: PrimitiveType;
  size?: number;
  scale?: number;
  color?: Brush;
  // CTA-specific
  text?: string;
  variableBinding?: string; // '{{cta}}'
  // Star field specific
  starCount?: number;
  // Additional props for flexibility
  props?: Record<string, unknown>;
}

// Primitive layer
export interface PrimitiveLayer extends BaseLayer {
  type: 'primitive';
  content: PrimitiveContent;
}

// Group layout options
export interface GroupLayout {
  type: 'stack' | 'row' | 'free';
  gap?: number;
  align?: 'start' | 'center' | 'end' | 'stretch';
  justify?: 'start' | 'center' | 'end' | 'space-between' | 'space-around';
  padding?: number | { top?: number; right?: number; bottom?: number; left?: number };
}

// Group layer - container for nested layers
export interface GroupLayer extends BaseLayer {
  type: 'group';
  children: Layer[];
  layout?: GroupLayout;
}

// Union type for all layer types
export type Layer = TextLayer | ImageLayer | VideoLayer | AudioLayer | ShapeLayer | SvgPathLayer | LottieLayer | RiveLayer | PrimitiveLayer | GroupLayer;

// Type guards
export const isTextLayer = (layer: Layer): layer is TextLayer => layer.type === 'text';
export const isImageLayer = (layer: Layer): layer is ImageLayer => layer.type === 'image';
export const isVideoLayer = (layer: Layer): layer is VideoLayer => layer.type === 'video';
export const isAudioLayer = (layer: Layer): layer is AudioLayer => layer.type === 'audio';
export const isShapeLayer = (layer: Layer): layer is ShapeLayer => layer.type === 'shape';
export const isSvgPathLayer = (layer: Layer): layer is SvgPathLayer => layer.type === 'svg-path';
export const isLottieLayer = (layer: Layer): layer is LottieLayer => layer.type === 'lottie';
export const isRiveLayer = (layer: Layer): layer is RiveLayer => layer.type === 'rive';
export const isPrimitiveLayer = (layer: Layer): layer is PrimitiveLayer => layer.type === 'primitive';
export const isGroupLayer = (layer: Layer): layer is GroupLayer => layer.type === 'group';

// Layer factory functions
export const createTextLayer = (
  id: string,
  name: string,
  position: LayerPosition,
  content: Partial<TextContent> & { text: string }
): TextLayer => ({
  id,
  type: 'text',
  name,
  position,
  visible: true,
  locked: false,
  opacity: 1,
  content: {
    fontSize: 24,
    fontFamily: 'Inter',
    fontWeight: 400,
    color: { type: 'solid', color: '#FFFFFF' },
    textAlign: 'center',
    ...content,
  },
});

export const createPrimitiveLayer = (
  id: string,
  name: string,
  position: LayerPosition,
  primitiveType: PrimitiveType,
  content?: Partial<Omit<PrimitiveContent, 'primitiveType'>>
): PrimitiveLayer => ({
  id,
  type: 'primitive',
  name,
  position,
  visible: true,
  locked: false,
  opacity: 1,
  content: {
    primitiveType,
    ...content,
  },
});

export const createGroupLayer = (
  id: string,
  name: string,
  position: LayerPosition,
  children: Layer[] = [],
  layout?: GroupLayout
): GroupLayer => ({
  id,
  type: 'group',
  name,
  position,
  visible: true,
  locked: false,
  opacity: 1,
  children,
  layout,
});

export const createSvgPathLayer = (
  id: string,
  name: string,
  position: LayerPosition,
  commands: SvgCommand[],
  content?: Partial<Omit<SvgPathContent, 'commands'>>
): SvgPathLayer => ({
  id,
  type: 'svg-path',
  name,
  position,
  visible: true,
  locked: false,
  opacity: 1,
  content: {
    commands,
    fill: { type: 'transparent' },
    stroke: { type: 'solid', color: '#FFFFFF' },
    strokeWidth: 2,
    ...content,
  },
});

export const createLottieLayer = (
  id: string,
  name: string,
  position: LayerPosition,
  content: LottieContent
): LottieLayer => ({
  id,
  type: 'lottie',
  name,
  position,
  visible: true,
  locked: false,
  opacity: 1,
  content: {
    autoplay: true,
    loop: true,
    speed: 1,
    direction: 1,
    renderer: 'svg',
    ...content,
  },
});

export const createRiveLayer = (
  id: string,
  name: string,
  position: LayerPosition,
  src: string,
  content?: Partial<Omit<RiveContent, 'src'>>
): RiveLayer => ({
  id,
  type: 'rive',
  name,
  position,
  visible: true,
  locked: false,
  opacity: 1,
  content: {
    src,
    autoplay: true,
    fit: 'contain',
    alignment: 'center',
    ...content,
  },
});
